import { cn } from './cn';
import { Icon } from './Icon';

export interface SaveControlProps {
  saved?: boolean;
  onToggle?: (next: boolean) => void;
  /** `icon` for card corners and toolbars, `labelled` for action bars. */
  variant?: 'icon' | 'labelled';
  size?: number;
  className?: string;
  disabled?: boolean;
}

/**
 * Save / unsave.
 *
 * `.men` had a clear save affordance and `.app` had none — this is the shared
 * one. Saved state is pink because it is a selected state, which is on the
 * short list of jobs magenta is allowed to do.
 *
 * The label is always rendered for screen readers even in icon mode, so the
 * control is never an unlabelled glyph.
 */
export const SaveControl = ({
  saved = false,
  onToggle,
  variant = 'icon',
  size = 20,
  className,
  disabled,
}: SaveControlProps) => {
  const label = saved ? 'Saved — tap to remove' : 'Save';

  return (
    <button
      type="button"
      disabled={disabled}
      aria-pressed={saved}
      aria-label={variant === 'icon' ? label : undefined}
      onClick={() => onToggle?.(!saved)}
      className={cn(
        'inline-flex select-none items-center justify-center gap-2 rounded-sm',
        'transition-[color,background-color,border-color,transform] duration-base ease-mechanical',
        'active:scale-[0.97]',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-hottr-pink focus-visible:ring-offset-2 focus-visible:ring-offset-hottr-ink',
        'disabled:pointer-events-none disabled:opacity-40',
        variant === 'icon'
          ? 'h-11 w-11'
          : 'h-12 border border-white/[0.12] bg-white/[0.06] px-6 hottr-cta hover:border-white/[0.24] hover:bg-white/[0.10]',
        saved ? 'text-hottr-pink' : 'text-white/60 hover:text-white',
        className,
      )}
    >
      <Icon name="bookmark" size={size} filled={saved} />
      {variant === 'labelled' && <span>{saved ? 'Saved' : 'Save'}</span>}
    </button>
  );
};
